/**
 * 배포 화면 상단 "실행 중 버전" 표의 계산부 — 그리는 쪽(RunningVersionsPanel)과
 * 분리해 테스트한다.
 */
import { SITE_BATCH_SERVICE_CATALOG } from '../../../models/SiteBatch';
import type { ServiceVersion } from '../../../models/ServiceVersion';

export interface VersionRow {
  app: string;
  label: string;
  /** 열 순서대로 그 서버의 버전. 열을 모르거나 그 서버에 행이 없으면 null. */
  cells: Array<ServiceVersion | null>;
}

/**
 * 버전 표 행 — 배포 순서 8개 서비스 + 통합서버 offline-manage. offline-manage 는 컨테이너가
 * 아닌 systemd 바이너리라 배포 단계에는 없고 표에만 있다(사내 102 에는 없어 사내 칸은 비어 있다).
 */
const VERSION_ROWS = [...SITE_BATCH_SERVICE_CATALOG, { appname: 'offline-manage', label: 'Offline Manage' }];

/** 서비스 × 열(서버) 표를 만든다. 열이 null 이면 그 서버를 못 읽은 것. */
export function buildVersionRows(columns: Array<ServiceVersion[] | null>): VersionRow[] {
  return VERSION_ROWS.map(({ appname, label }) => ({
    app: appname,
    label,
    cells: columns.map((col) => col?.find((v) => v.app === appname) ?? null),
  }));
}

const known = (v?: ServiceVersion | null): v is ServiceVersion =>
  !!v && v.state !== 'missing' && v.source !== 'latest' && v.version !== '';

/**
 * 두 서버의 같은 서비스가 같은 빌드인지. 리비전(커밋)이 둘 다 있으면 리비전으로,
 * 아니면 버전 문자열로 본다. 한쪽이라도 버전 미상이면 단정하지 않는다(unknown).
 */
export function compareBuild(
  a?: ServiceVersion | null,
  b?: ServiceVersion | null,
): 'same' | 'different' | 'unknown' {
  if (!known(a) || !known(b)) return 'unknown';
  if (a.revision && b.revision) return a.revision === b.revision ? 'same' : 'different';
  return a.version === b.version ? 'same' : 'different';
}

/**
 * "현장 다시 읽기" 가 끝났는지 — 이 현장의 점검 결과(방재실마다 하나)가 모두 요청 전과 다른
 * 시각으로 바뀌었으면 끝. 서버·브라우저 시각이 어긋날 수 있어 시각 크기가 아니라 바뀌었는지로 본다.
 */
export function recheckDone(
  before: Record<string, string>,
  sites: Array<{ code: string; checked_at: string }>,
): boolean {
  return sites.length > 0 && sites.every((s) => !!s.checked_at && s.checked_at !== before[s.code]);
}

/** 시각 → "11/6 00:03" (현장 확인 시각·자동 배포 실행 시각). 비었거나 잘못된 값은 빈 문자열. */
export function clock(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 빌드 시각 → "10/21 빌드". 비었거나 잘못된 값은 빈 문자열. */
export function buildDateLabel(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} 빌드`;
}

export type HintTone = 'muted' | 'warn' | 'danger';

/** 칸 아래 한 줄 — 상태가 비정상이면 상태, 아니면 버전을 어디서 읽었는지. */
export function sourceHint(v: ServiceVersion): { text: string; tone: HintTone } {
  if (v.state === 'missing') return { text: '컨테이너 없음', tone: 'danger' };
  if (v.state !== 'running' && v.state !== 'static') return { text: v.state, tone: 'danger' };
  switch (v.source) {
    case 'latest':
      return { text: '버전 미상', tone: 'warn' };
    case 'label':
      return { text: '빌드 라벨', tone: 'muted' };
    case 'release':
      return { text: '정적 릴리즈', tone: 'muted' };
    case 'commit':
      return { text: '커밋 빌드', tone: 'muted' };
    case 'commit-dirty':
      return { text: '커밋 + 미커밋 수정', tone: 'muted' };
    default:
      return { text: '이미지 태그', tone: 'muted' };
  }
}
